import React from 'react';
import styled from 'styled-components';
import { Search, Filter, X } from 'lucide-react';
import { Project, ProjectFilter } from '../types';
import { safeGet } from '../utils/errorHandling';
import { handleError } from '../utils/errorHandling';

const FiltersContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 300px;
  height: 100vh;
  background: ${({ theme }) => theme.colors.white};
  z-index: 1000;
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 100%;
  }
`;

const FilterContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[3]};
  }
`;

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.white};
  position: sticky;
  top: 0;
  z-index: 2;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[3]};
  }
`;

const FilterTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: ${({ theme }) => theme.typography.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing[2]};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.full};

  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
  }
`;

const FilterSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const FilterLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: ${({ theme }) => theme.typography.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const FilterInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sm};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sm};
  background: ${({ theme }) => theme.colors.white};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const FilterCheckbox = styled.input`
  margin-right: ${({ theme }) => theme.spacing[2]};
`;

const FilterCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  cursor: pointer;
`;

const StatusCheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const StatusCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
  }

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    margin: 0;
  }

  label {
    font-size: ${({ theme }) => theme.typography.sm};
    color: ${({ theme }) => theme.colors.text};
    cursor: pointer;
    margin: 0;
  }
`;

const SearchBar = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sm};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: ${({ theme }) => theme.spacing[2]};
  top: 50%;
  transform: translateY(-50%);
`;

const FilterFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.white};
  position: sticky;
  bottom: 0;
  z-index: 2;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[3]};
  }
`;

const FilterCount = styled.span`
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: ${({ theme }) => theme.typography.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 150px;
  }
`;

const FilterButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing[2]};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.full};

  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
  }
`;

const DateRangeContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

export interface ProjectFiltersProps {
  filters: ProjectFilter;
  onFilterChange: (filters: Partial<ProjectFilter>) => void;
  totalProjects: number;
  filteredProjects: number;
  isOpen: boolean;
  onClose: () => void;
}

const ProjectFilters: React.FC<ProjectFiltersProps> = ({
  filters,
  onFilterChange,
  totalProjects,
  filteredProjects,
  isOpen,
  onClose
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    onFilterChange({ ...filters, [name]: value });
  };

  const handleStatusChange = (status: string) => {
    const newStatus = filters.status.includes(status)
      ? filters.status.filter(s => s !== status)
      : [...filters.status, status];
    onFilterChange({ ...filters, status: newStatus });
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const [field, order] = e.target.value.split('-');
    const sortBy = field as 'created_at' | 'customer_name' | 'order_id';
    const sortOrder = order as 'asc' | 'desc';
    onFilterChange({ ...filters, sortBy, sortOrder });
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    onFilterChange({ ...filters, [name]: value || null });
  };

  const handleClearFilters = () => {
    onFilterChange({
      searchTerm: '',
      status: [],
      customerName: '',
      orderId: '',
      projectId: '',
      startDate: null,
      endDate: null,
      sortBy: 'created_at',
      sortOrder: 'desc'
    });
  };

  return (
    <FiltersContainer $isOpen={isOpen}>
      <FilterHeader>
        <FilterTitle>Filters</FilterTitle>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>
      </FilterHeader>

      <FilterContent>
        <FilterSection>
          <FilterLabel>Search</FilterLabel>
          <SearchBar>
            <SearchInput
              type="text"
              placeholder="Search projects..."
              value={filters.searchTerm}
              onChange={(e) => onFilterChange({ searchTerm: e.target.value })}
            />
            <SearchIcon>
              <Search size={16} />
            </SearchIcon>
          </SearchBar>
        </FilterSection>

        <FilterSection>
          <FilterLabel>Status</FilterLabel>
          <StatusCheckboxGroup>
            <StatusCheckbox>
              <input
                type="checkbox"
                id="status-pending"
                checked={filters.status.includes('pending')}
                onChange={() => handleStatusChange('pending')}
              />
              <label htmlFor="status-pending">Pending</label>
            </StatusCheckbox>
            <StatusCheckbox>
              <input
                type="checkbox"
                id="status-in-progress"
                checked={filters.status.includes('in-progress')}
                onChange={() => handleStatusChange('in-progress')}
              />
              <label htmlFor="status-in-progress">In Progress</label>
            </StatusCheckbox>
            <StatusCheckbox>
              <input
                type="checkbox"
                id="status-completed"
                checked={filters.status.includes('completed')}
                onChange={() => handleStatusChange('completed')}
              />
              <label htmlFor="status-completed">Completed</label>
            </StatusCheckbox>
            <StatusCheckbox>
              <input
                type="checkbox"
                id="status-cancelled"
                checked={filters.status.includes('cancelled')}
                onChange={() => handleStatusChange('cancelled')}
              />
              <label htmlFor="status-cancelled">Cancelled</label>
            </StatusCheckbox>
          </StatusCheckboxGroup>
        </FilterSection>

        <FilterSection>
          <FilterLabel>Project Code</FilterLabel>
          <FilterInput
            type="text"
            name="projectId"
            placeholder="Enter project code"
            value={filters.projectId}
            onChange={handleInputChange}
          />
        </FilterSection>

        <FilterSection>
          <FilterLabel>Customer Name</FilterLabel>
          <FilterInput
            type="text"
            name="customerName"
            placeholder="Enter customer name"
            value={filters.customerName}
            onChange={handleInputChange}
          />
        </FilterSection>

        <FilterSection>
          <FilterLabel>Order ID</FilterLabel>
          <FilterInput
            type="text"
            name="orderId"
            placeholder="Enter order ID"
            value={filters.orderId}
            onChange={handleInputChange}
          />
        </FilterSection>

        <FilterSection>
          <FilterLabel>Date Range</FilterLabel>
          <DateRangeContainer>
            <FilterInput
              type="date"
              name="startDate"
              value={filters.startDate ? new Date(filters.startDate).toISOString().split('T')[0] : ''}
              onChange={handleDateChange}
            />
            <FilterInput
              type="date"
              name="endDate"
              value={filters.endDate ? new Date(filters.endDate).toISOString().split('T')[0] : ''}
              onChange={handleDateChange}
            />
          </DateRangeContainer>
        </FilterSection>

        <FilterSection>
          <FilterLabel>Sort By</FilterLabel>
          <FilterSelect
            value={`${filters.sortBy}-${filters.sortOrder}`}
            onChange={handleSortChange}
          >
            <option value="created_at-desc">Newest First</option>
            <option value="created_at-asc">Oldest First</option>
            <option value="customer_name-asc">Customer Name (A-Z)</option>
            <option value="customer_name-desc">Customer Name (Z-A)</option>
            <option value="order_id-asc">Order ID (A-Z)</option>
            <option value="order_id-desc">Order ID (Z-A)</option>
          </FilterSelect>
        </FilterSection>
      </FilterContent>

      <FilterFooter>
        <FilterCount>
          {filteredProjects === totalProjects 
            ? `${totalProjects} projects`
            : `${filteredProjects}/${totalProjects} projects`
          }
        </FilterCount>
        <FilterButton onClick={handleClearFilters}>
          Clear Filters
        </FilterButton>
      </FilterFooter>
    </FiltersContainer>
  );
};

export default ProjectFilters; 